import { z } from 'zod';

/**
 * Validation for the RFQ wizard.
 *
 * DELIBERATELY MIRRORS `StoreRfqRequest`, and the duplication is the point: the
 * server's copy is the security boundary and this one exists so a salesperson finds
 * out about a problem while typing rather than after submitting. Where the two could
 * drift, the server wins — it is the only one that can be trusted.
 *
 * The cross-field rules are the interesting part. Each encodes something about
 * freight rather than about forms:
 *
 *   hazardous ⇒ UN number   hazardous cargo cannot legally move without one, and the
 *                           database has a CHECK constraint saying so
 *   delivery ≥ ready        cargo cannot be delivered before it exists
 *   at least one line       an RFQ with no lines has nothing to price
 */

/** Step 1 — who and what service. */
export const routeStep = z.object({
  customer_id: z.string().uuid('Choose a customer.'),
  service_category: z.string().min(1, 'Choose a service.'),
  origin_description: z.string().min(1, 'Where does it ship from?').max(255),
  origin_city: z.string().max(255).optional(),
  destination_description: z.string().min(1, 'Where does it ship to?').max(255),
  destination_city: z.string().max(255).optional(),
  // Optional: blank means "estimate it from the coordinates".
  distance_km: z.coerce.number().min(0).max(50000).optional(),
});

/** Step 2 — the cargo. */
export const cargoStep = z
  .object({
    cargo_type: z.string().min(1),
    incoterm: z.string().optional(),
    // Gross weight, in kilograms. `gt(0)` not `min(0)`: zero is a mistake, not a
    // free shipment.
    weight_kg: z.coerce.number().gt(0, 'Weight is what freight is charged on.').max(500000),
    volume_m3: z.coerce.number().min(0).max(10000).optional(),
    piece_count: z.coerce.number().int().min(1).max(100000).optional(),
    currency: z.string().length(3),
    // MAJOR units in the form; converted to minor units on submit, as everywhere
    // else money is entered.
    declared_value: z.coerce.number().min(0).optional(),
    temperature_controlled: z.boolean().default(false),
    hazardous: z.boolean().default(false),
    oversized: z.boolean().default(false),
    high_value: z.boolean().default(false),
    un_number: z.string().max(10).optional(),
  })
  .refine((data) => !data.hazardous || (data.un_number ?? '').trim().length > 0, {
    message: 'Hazardous cargo needs its UN number.',
    path: ['un_number'],
  })
  .refine((data) => data.hazardous || (data.un_number ?? '').trim().length === 0, {
    message: 'A UN number only applies to hazardous cargo.',
    path: ['un_number'],
  });

/** Step 3 — lines and dates. */
export const detailStep = z
  .object({
    line_items: z
      .array(
        z.object({
          description: z.string().min(1, 'Describe the line.').max(255),
          quantity: z.coerce.number().gt(0),
          unit_of_measure: z.string().min(1),
          product_id: z.string().uuid().optional(),
        }),
      )
      // The server refuses submission without one; saying so here avoids a
      // round trip to learn it.
      .min(1, 'Add at least one line — there would be nothing to price.'),
    ready_date: z.string().optional(),
    required_delivery_date: z.string().optional(),
    respond_by: z.string().optional(),
    notes: z.string().max(5000).optional(),
  })
  .refine(
    (data) =>
      !data.ready_date ||
      !data.required_delivery_date ||
      data.required_delivery_date >= data.ready_date,
    {
      message: 'Delivery cannot be required before the cargo is ready.',
      path: ['required_delivery_date'],
    },
  );

export const rfqSchema = routeStep.and(cargoStep).and(detailStep);

export type RouteStepValues = z.infer<typeof routeStep>;
export type CargoStepValues = z.infer<typeof cargoStep>;
export type DetailStepValues = z.infer<typeof detailStep>;
export type RfqFormValues = z.infer<typeof rfqSchema>;

/**
 * Volumetric weight and the greater-of rule, mirrored client-side.
 *
 * The SERVER computes and stores the authoritative figure — this is here so the
 * wizard can show it while someone types, which is the only moment it changes a
 * decision. Divisors match `ChargeableWeight::DIVISORS`; if they ever disagree the
 * server's number is the one that reaches a quote.
 */
const DIVISORS: Record<string, number> = {
  air: 167,
  sea_lcl: 1000,
  sea_fcl: 1000,
  road_ftl: 333,
  road_ltl: 333,
  rail: 333,
  last_mile: 333,
};

const VOLUME_EXEMPT_CARGO = ['bulk_dry', 'bulk_liquid', 'containerised'];

export interface ChargeableWeight {
  chargeable: number;
  basis: 'actual' | 'volumetric' | 'not_applicable';
  divisor: number | null;
}

export function chargeableWeight(
  weightKg: number,
  volumeM3: number | undefined,
  serviceCategory: string,
  cargoType: string,
): ChargeableWeight {
  const divisor = DIVISORS[serviceCategory] ?? null;

  if (!volumeM3 || volumeM3 <= 0 || divisor === null || VOLUME_EXEMPT_CARGO.includes(cargoType)) {
    return { chargeable: weightKg, basis: 'not_applicable', divisor };
  }

  const volumetric = volumeM3 * divisor;

  return volumetric > weightKg
    ? { chargeable: volumetric, basis: 'volumetric', divisor }
    : { chargeable: weightKg, basis: 'actual', divisor };
}
